/* eslint-disable jsx-a11y/iframe-has-title */
import React, { useState, useEffect } from "react";
import * as emailjs from "@emailjs/browser";
import "@n8n/chat/style.css";
import { createChat } from "@n8n/chat";
import {
  Globe,
  Mic,
  ShieldCheck,
  ArrowRight,
  Layers,
  ExternalLink,
  Menu,
  X,
  Zap,
  Smartphone,
  Brain,
  Lock,
  UserCheck,
  ShoppingBag,
  TrendingUp,
  Briefcase,
  QrCode,
  Trophy,
  Megaphone,
  Mail,
  MapPin,
  Linkedin,
  Twitter,
  Github,
  Wifi,
  Workflow,
} from "lucide-react";
import { initializeApp } from "firebase/app";
import { getAnalytics, logEvent } from "firebase/analytics";

// Your web app's Firebase configuration
// For Firebase JS SDK v7.20.0 and later, measurementId is optional
const firebaseConfig = {
  apiKey: "AIzaSyBRqC2pWymPE3IBY2wgkUUwunpYkiJbyAA",
  authDomain: "pseudoclan-b122f.firebaseapp.com",
  projectId: "pseudoclan-b122f",
  storageBucket: "pseudoclan-b122f.appspot.com",
  messagingSenderId: "517629311854",
  appId: "1:517629311854:web:0fb6560324e1807ab11f7f",
  measurementId: "G-2NRFHYW2Z9",
};

const app = initializeApp(firebaseConfig);
const analytics = getAnalytics(app);

// Navigation Component
const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const navLinks = [
    { name: "Products", href: "#products" },
    { name: "Innovation Lab", href: "#innovation" },
    { name: "Capabilities", href: "#capabilities" },
    { name: "Contact", href: "#contact" },
  ];

  return (
    <nav
      className={`fixed w-full z-50 transition-all duration-300 ${scrolled ? "bg-gray-950/90 backdrop-blur-md border-b border-gray-800 py-4" : "bg-transparent py-6"}`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex justify-between items-center">
        <div className="flex items-center space-x-2">
          <div className="w-[208px] bg-white h-10 rounded-lg flex items-center justify-center p-2">
            <img src="logo-pc.svg" className="App-logo" alt="logo" />{" "}
          </div>
        </div>

        {/* Desktop Nav */}
        <div className="hidden md:flex space-x-8">
          {navLinks.map((link) => (
            <a
              key={link.name}
              href={link.href}
              className="text-sm font-medium text-gray-300 hover:text-white transition-colors"
            >
              {link.name}
            </a>
          ))}
        </div>

        {/* Mobile Button */}
        <div className="md:hidden">
          <button
            onClick={() => setIsOpen(!isOpen)}
            className="text-gray-300 hover:text-white"
          >
            {isOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {isOpen && (
        <div className="md:hidden absolute top-full left-0 w-full bg-gray-900 border-b border-gray-800">
          <div className="px-4 pt-2 pb-4 space-y-1">
            {navLinks.map((link) => (
              <a
                key={link.name}
                href={link.href}
                className="block px-3 py-2 text-base font-medium text-gray-300 hover:text-white hover:bg-gray-800 rounded-md"
                onClick={() => setIsOpen(false)}
              >
                {link.name}
              </a>
            ))}
          </div>
        </div>
      )}
    </nav>
  );
};

// Hero Section
const Hero = () => {
  return (
    <section className="relative min-h-screen flex items-center justify-center pt-20 overflow-hidden">
      {/* Background Effects */}
      <div className="absolute inset-0 bg-grid z-0 opacity-20 pointer-events-none"></div>
      <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-blue-600/20 rounded-full blur-3xl pointer-events-none"></div>
      <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-purple-600/20 rounded-full blur-3xl pointer-events-none"></div>

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
        <div className="inline-flex items-center px-3 py-1 rounded-full border border-blue-500/30 bg-blue-500/10 mb-8">
          <span className="flex h-2 w-2 rounded-full bg-blue-400 mr-2 animate-pulse"></span>
          <span className="text-xs font-medium text-blue-300 tracking-wide uppercase">
            Innovating for Bharat & Beyond
          </span>
        </div>

        <h1 className="text-5xl md:text-7xl font-bold tracking-tight mb-6 leading-tight text-white">
          Architecting the Future of <br />
          <span className="gradient-text">Digital Intelligence</span>
        </h1>

        <p className="mt-4 max-w-2xl mx-auto text-xl text-gray-400">
          From Sovereign AI to Rural Inclusion. We bridge the gap between
          theoretical research and real-world application.
        </p>

        <div className="mt-10 flex flex-col sm:flex-row justify-center gap-4">
          <a
            href="#products"
            className="px-8 py-3 rounded-lg bg-blue-600 hover:bg-blue-500 text-white font-medium transition-all shadow-lg shadow-blue-600/20 flex items-center justify-center group"
          >
            Explore Products
            <ArrowRight
              size={18}
              className="ml-2 group-hover:translate-x-1 transition-transform"
            />
          </a>
          <a
            href="#innovation"
            className="px-8 py-3 rounded-lg border border-gray-700 hover:bg-gray-800 text-gray-300 font-medium transition-all flex items-center justify-center"
          >
            View R&D Labs
          </a>
        </div>
      </div>
    </section>
  );
};

// Live Products Section
const Products = () => {
  const products = [
    {
      title: "AI Interview Platform",
      desc: "Automate recruitment with voice AI. Real-time screening, CV parsing, and unbiased scoring.",
      link: "https://ai-interview.pseudoclan.com",
      icon: Mic,
      color: "text-blue-400",
      tags: ["ElevenLabs", "n8n", "React"],
    },
    {
      title: "Innothon",
      desc: "The complete innovation management suite. Streamline hackathons from registration to evaluation.",
      link: "https://innothon.pseudoclan.com",
      icon: Trophy,
      color: "text-purple-400",
      tags: ["SaaS", "Management", "Community"],
    },
    {
      title: "Qliky",
      desc: "Next-gen QR engagement tool. Dynamic generation with deep analytics for modern marketing.",
      link: "https://qliky.pseudoclan.com",
      icon: QrCode,
      color: "text-yellow-400",
      tags: ["Marketing", "Analytics", "Dynamic"],
    },
    {
      title: "DigiX Marketing",
      desc: "Your AI Companion for content creation. Boost your marketing game with high-quality, AI-generated content.",
      link: "https://play.google.com/store/apps/details?id=com.genxconnect",
      icon: Megaphone,
      color: "text-rose-400",
      tags: ["AI Content", "1k+ Downloads", "5.0 ★"],
    },
  ];

  return (
    <section id="products" className="py-24 bg-gray-950 relative">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mb-16">
          <h2 className="text-3xl md:text-4xl font-bold mb-4 text-white">
            Live Ecosystem
          </h2>
          <div className="h-1 w-20 bg-blue-600 rounded-full"></div>
          <p className="mt-4 text-gray-400 max-w-2xl">
            Market-ready platforms currently driving efficiency and engagement
            for our clients.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
          {products.map((product, idx) => (
            <div
              key={idx}
              className={`glass-card rounded-2xl p-8 hover:-translate-y-2 transition-transform duration-300 glow-hover group relative overflow-hidden`}
            >
              <div
                className={`absolute top-0 right-0 p-4 opacity-10 group-hover:opacity-20 transition-opacity ${product.color}`}
              >
                <product.icon size={100} />
              </div>

              <div
                className={`w-12 h-12 rounded-lg bg-gray-800 flex items-center justify-center mb-6 ${product.color}`}
              >
                <product.icon size={24} />
              </div>

              <h3 className="text-xl font-bold mb-3 text-white">
                {product.title}
              </h3>
              <p className="text-gray-400 mb-6 text-sm leading-relaxed">
                {product.desc}
              </p>

              <div className="flex flex-wrap gap-2 mb-8">
                {product.tags.map((tag) => (
                  <span
                    key={tag}
                    className="text-xs px-2 py-1 rounded bg-gray-800 border border-gray-700 text-gray-300"
                  >
                    {tag}
                  </span>
                ))}
              </div>

              <a
                onClick={() => {
                  logEvent(analytics, "product_view", {
                    content_type: "link",
                    content_id: product.title,
                  });
                }}
                href={product.link}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center text-sm font-semibold text-white hover:text-blue-400 transition-colors"
              >
                Launch Application <ExternalLink size={14} className="ml-2" />
              </a>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Innovation Lab (R&D) Section
const Innovation = () => {
  return (
    <section
      id="innovation"
      className="py-24 bg-gray-900 border-y border-gray-800"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <span className="text-blue-500 font-mono text-sm tracking-wider uppercase">
            Research & Development
          </span>
          <h2 className="text-3xl md:text-5xl font-bold mt-2 mb-6 text-white">
            Innovation Lab
          </h2>
          <p className="text-gray-400 max-w-2xl mx-auto">
            Where we solve the "Black Box" problem and build for the next
            billion users.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          {/* Satya-Lekha */}
          <div className="glass-card rounded-2xl overflow-hidden border border-blue-900/30">
            <div className="bg-gradient-to-r from-blue-900/50 to-gray-900 p-8 border-b border-gray-800">
              <div className="flex justify-between items-start">
                <div>
                  <h3 className="text-2xl font-bold text-white mb-1">
                    Satya-Lekha
                  </h3>
                  <p className="text-blue-400 font-mono text-xs">
                    TRUTHFUL ACCOUNTING ENGINE
                  </p>
                </div>
                <ShieldCheck className="text-blue-500" size={32} />
              </div>
            </div>
            <div className="p-8">
              <p className="text-gray-300 mb-6">
                A sovereign, on-premise AI engine designed to automate financial
                compliance. Unlike standard LLMs, Satya-Lekha prioritizes
                auditability over creativity.
              </p>
              <ul className="space-y-3 mb-6">
                <li className="flex items-start text-sm text-gray-400">
                  <Lock size={16} className="mr-2 mt-0.5 text-blue-500" />
                  <span>
                    <strong>100% On-Premise:</strong> No financial data leaves
                    the secure network.
                  </span>
                </li>
                <li className="flex items-start text-sm text-gray-400">
                  <Brain size={16} className="mr-2 mt-0.5 text-blue-500" />
                  <span>
                    <strong>Explainable AI (XAI):</strong> Every flag cites
                    specific SEBI/RBI regulation clauses.
                  </span>
                </li>
                <li className="flex items-start text-sm text-gray-400">
                  <Globe size={16} className="mr-2 mt-0.5 text-blue-500" />
                  <span>
                    <strong>Multilingual:</strong> Native processing of Hindi &
                    Tamil documents.
                  </span>
                </li>
              </ul>
              <div className="flex gap-2">
                <span className="px-2 py-1 bg-gray-800 rounded text-xs text-gray-400">
                  Custom FineTune Small Model
                </span>
                <span className="px-2 py-1 bg-gray-800 rounded text-xs text-gray-400">
                  OCR
                </span>
                <span className="px-2 py-1 bg-gray-800 rounded text-xs text-gray-400">
                  Docker
                </span>
              </div>
            </div>
          </div>

          {/* Bharat FinLit */}
          <div className="glass-card rounded-2xl overflow-hidden border border-green-900/30">
            <div className="bg-gradient-to-r from-green-900/40 to-gray-900 p-8 border-b border-gray-800">
              <div className="flex justify-between items-start">
                <div>
                  <h3 className="text-2xl font-bold text-white mb-1">
                    FinSafar
                  </h3>
                  <p className="text-green-400 font-mono text-xs">
                    GAMIFIED INCLUSION
                  </p>
                </div>
                <Smartphone className="text-green-500" size={32} />
              </div>
            </div>
            <div className="p-8">
              <p className="text-gray-300 mb-6">
                An offline-first platform bridging the financial literacy gap
                for rural India ("Bharat") through simulation-based learning and
                voice AI.
              </p>
              <ul className="space-y-3 mb-6">
                <li className="flex items-start text-sm text-gray-400">
                  <Zap size={16} className="mr-2 mt-0.5 text-green-500" />
                  <span>
                    <strong>Offline-First:</strong> Built on WatermelonDB for
                    low-connectivity zones.
                  </span>
                </li>
                <li className="flex items-start text-sm text-gray-400">
                  <Mic size={16} className="mr-2 mt-0.5 text-green-500" />
                  <span>
                    <strong>Voice-Enabled:</strong> Text-to-Speech &
                    Speech-to-Intent for oral cultures.
                  </span>
                </li>
                <li className="flex items-start text-sm text-gray-400">
                  <Layers size={16} className="mr-2 mt-0.5 text-green-500" />
                  <span>
                    <strong>Simulations:</strong> "Kisan Chakra" farming & risk
                    management games.
                  </span>
                </li>
              </ul>
              <div className="flex gap-2">
                <span className="px-2 py-1 bg-gray-800 rounded text-xs text-gray-400">
                  React Native
                </span>
                <span className="px-2 py-1 bg-gray-800 rounded text-xs text-gray-400">
                  WatermelonDB
                </span>
                <span className="px-2 py-1 bg-gray-800 rounded text-xs text-gray-400">
                  Node.js
                </span>
              </div>
            </div>
          </div>

          {/* Kamai Kavach */}
          <div className="glass-card rounded-2xl overflow-hidden border border-orange-900/30">
            <div className="bg-gradient-to-r from-orange-900/40 to-gray-900 p-8 border-b border-gray-800">
              <div className="flex justify-between items-start">
                <div>
                  <h3 className="text-2xl font-bold text-white mb-1">
                    Kamai Kavach
                  </h3>
                  <p className="text-orange-400 font-mono text-xs">
                    GIG WORKER DIGITAL SHIELD
                  </p>
                </div>
                <UserCheck className="text-orange-500" size={32} />
              </div>
            </div>
            <div className="p-8">
              <p className="text-gray-300 mb-6">
                A digital identity and welfare aggregation platform designed to
                provide social security, verified credentials, and financial
                resilience to India's gig economy workers.
              </p>
              <ul className="space-y-3 mb-6">
                <li className="flex items-start text-sm text-gray-400">
                  <Briefcase
                    size={16}
                    className="mr-2 mt-0.5 text-orange-500"
                  />
                  <span>
                    <strong>Digital Identity:</strong> Portable, verified
                    profile integrating personal and work details.
                  </span>
                </li>
                <li className="flex items-start text-sm text-gray-400">
                  <ShieldCheck
                    size={16}
                    className="mr-2 mt-0.5 text-orange-500"
                  />
                  <span>
                    <strong>Welfare Access:</strong> Automated discovery and
                    application for schemes & insurance.
                  </span>
                </li>
                <li className="flex items-start text-sm text-gray-400">
                  <Globe size={16} className="mr-2 mt-0.5 text-orange-500" />
                  <span>
                    <strong>Financial Shield:</strong> Micro-loans, accident
                    insurance, and NPS-Lite pension.
                  </span>
                </li>
              </ul>
              <div className="flex gap-2">
                <span className="px-2 py-1 bg-gray-800 rounded text-xs text-gray-400">
                  Identity Stack
                </span>
                <span className="px-2 py-1 bg-gray-800 rounded text-xs text-gray-400">
                  DigiLocker
                </span>
                <span className="px-2 py-1 bg-gray-800 rounded text-xs text-gray-400">
                  Multilingual
                </span>
              </div>
            </div>
          </div>

          {/* UREP */}
          <div className="glass-card rounded-2xl overflow-hidden border border-pink-900/30">
            <div className="bg-gradient-to-r from-pink-900/40 to-gray-900 p-8 border-b border-gray-800">
              <div className="flex justify-between items-start">
                <div>
                  <h3 className="text-2xl font-bold text-white mb-1">UREP</h3>
                  <p className="text-pink-400 font-mono text-xs">
                    UNIFIED RETAIL ECOSYSTEM
                  </p>
                </div>
                <ShoppingBag className="text-pink-500" size={32} />
              </div>
            </div>
            <div className="p-8">
              <p className="text-gray-300 mb-6">
                Bridging the digital divide in B2B commerce by connecting
                manufacturers, distributors, and SMEs through a unified Retail
                4.0 infrastructure.
              </p>
              <ul className="space-y-3 mb-6">
                <li className="flex items-start text-sm text-gray-400">
                  <TrendingUp size={16} className="mr-2 mt-0.5 text-pink-500" />
                  <span>
                    <strong>Retail 4.0:</strong> Democratizing enterprise-grade
                    demand forecasting for SMEs.
                  </span>
                </li>
                <li className="flex items-start text-sm text-gray-400">
                  <Layers size={16} className="mr-2 mt-0.5 text-pink-500" />
                  <span>
                    <strong>Supply Chain:</strong> Real-time visibility
                    connecting unorganized and organized trade.
                  </span>
                </li>
                <li className="flex items-start text-sm text-gray-400">
                  <Zap size={16} className="mr-2 mt-0.5 text-pink-500" />
                  <span>
                    <strong>B2B Gamification:</strong> Incentivizing digital
                    adoption through behavioral mechanics.
                  </span>
                </li>
              </ul>
              <div className="flex gap-2">
                <span className="px-2 py-1 bg-gray-800 rounded text-xs text-gray-400">
                  Algorithmic Pricing
                </span>
                <span className="px-2 py-1 bg-gray-800 rounded text-xs text-gray-400">
                  B2B Commerce
                </span>
                <span className="px-2 py-1 bg-gray-800 rounded text-xs text-gray-400">
                  Analytics
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// About

export function AboutCapabilities() {
  const capabilities = [
    {
      icon: Brain,
      title: "Generative & Voice AI",
      description:
        "Expertise in deploying LLMs (Llama 3), Voice-to-Text pipelines (Whisper, ElevenLabs), and localized language models for Indian contexts.",
    },
    {
      icon: Wifi,
      title: "Offline-First Engineering",
      description:
        'Pioneering "Lite" architectures using WatermelonDB and React Native to ensure applications work seamlessly in low-connectivity zones.',
    },
    {
      icon: ShieldCheck,
      title: "RegTech & Compliance",
      description:
        'Building "Glass Box" explainable AI architectures that prioritize auditability, data sovereignty, and precision over hallucination.',
    },
    {
      icon: Workflow,
      title: "Workflow Automation",
      description:
        "End-to-end process orchestration using tools like n8n and custom API integrations to streamline business logic.",
    },
  ];
  return (
    <section className="py-24 bg-slate-50">
      <div className="max-w-7xl mx-auto px-6">
        {/* About Section */}
        <div className="mb-20">
          <h2 className="text-4xl md:text-5xl font-bold text-slate-900 mb-6">
            Who We Are
          </h2>
          <p className="text-lg text-slate-700 leading-relaxed">
            <strong>Pseudoclan Private Limited</strong> is a technology
            innovation firm dedicated to solving complex, real-world problems
            through cutting-edge software and Artificial Intelligence. We bridge
            the gap between theoretical AI and practical application, delivering
            solutions that range from automated corporate recruitment to
            financial inclusion for the underserved.
          </p>
        </div>

        {/* Capabilities Section */}
        <div>
          <h2 className="text-4xl md:text-5xl font-bold text-slate-900 mb-4">
            Core Capabilities
          </h2>
          <p className="text-lg text-slate-700 mb-12 max-w-4xl">
            Our team combines deep research with agile development to deliver
            scalable solutions across four pillars:
          </p>

          <div className="grid md:grid-cols-2 gap-8">
            {capabilities.map((capability, index) => {
              const Icon = capability.icon;
              return (
                <div
                  key={index}
                  className="bg-white p-8 rounded-xl shadow-sm hover:shadow-md transition-shadow border border-slate-200"
                >
                  <div className="flex items-start gap-4">
                    <div className="flex-shrink-0 w-12 h-12 bg-blue-600 rounded-lg flex items-center justify-center">
                      <Icon className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <h3 className="text-xl font-semibold text-slate-900 mb-3">
                        {capability.title}
                      </h3>
                      <p className="text-slate-600 leading-relaxed">
                        {capability.description}
                      </p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
}

// Tech Stack
const Capabilities = () => {
  const stack = [
    {
      category: "Generative AI",
      tools: [
        "AI4Bharat",
        "Gemeni",
        "OpenAI",
        "Ollama",
        "vLlm",
        "NanoBanana",
        "ElevenLabs",
        "Whisper",
        "LangChain",
      ],
    },
    {
      category: "Frontend",
      tools: [
        "React js",
        "TypeScript",
        "Tailwind",
        "React Native",
        "Flutter",
        "NextJS",
        "Angular",
        "Vue",
      ],
    },
    {
      category: "Backend & Infra",
      tools: [
        "Node.js",
        "DotNet",
        "Django",
        "Fast Api",
        "Docker",
        "Kubernetes",
        "MongoDb",
        "PostgreSql",
        "MariaDb",
        "AWS",
        "GCloud",
      ],
    },
    { category: "Workflow", tools: ["n8n", "PostHog", "WebRTC", "REST APIs"] },
  ];

  return (
    <section id="capabilities" className="py-24 bg-gray-950">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mb-12">
          <h2 className="text-3xl font-bold mb-4 text-white">
            Our Technology Stack
          </h2>
          <p className="text-gray-400">
            Our engineering DNA. Cutting-edge tools and frameworks powering our
            solutions
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          {stack.map((group, idx) => (
            <div key={idx} className="border-l-2 border-gray-800 pl-6 py-2">
              <h4 className="text-lg font-semibold text-white mb-4">
                {group.category}
              </h4>
              <ul className="space-y-2">
                {group.tools.map((tool) => (
                  <li
                    key={tool}
                    className="text-gray-400 text-sm flex items-center"
                  >
                    <div className="w-1.5 h-1.5 bg-blue-500 rounded-full mr-2"></div>
                    {tool}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
        {/* Additional Info */}
        <div className="mt-16 bg-gray-900 rounded-2xl p-8 md:p-12 text-white text-center">
          <h3 className="text-2xl md:text-3xl font-bold mb-4">
            Innovating Today for a Smarter Tomorrow
          </h3>
          <p className="text-blue-100 text-lg mb-8 max-w-2xl mx-auto">
            Our technology stack is carefully curated to deliver scalable,
            secure, and innovative solutions that meet the evolving needs of
            businesses and communities.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href="#contact"
              className="px-8 py-4 bg-white text-blue-600 rounded-lg font-medium hover:bg-blue-50 transition-colors"
            >
              Partner With Us
            </a>
            <a
              href="#products"
              className="px-8 py-4 bg-blue-500 text-white rounded-lg font-medium hover:bg-blue-400 transition-colors"
            >
              View Products
            </a>
          </div>
        </div>
      </div>
    </section>
  );
};

// Contact Us
export function Contact() {
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [message, setMessage] = useState("");
  const [companyName, setCompanyName] = useState("");
  return (
    <section id="contact" className="py-24 bg-white">
      <div className="max-w-7xl mx-auto px-6">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Left Column - Contact Info */}
          <div>
            <h2 className="text-4xl md:text-5xl font-bold text-slate-900 mb-6">
              Partner With Us
            </h2>
            <p className="text-lg text-slate-700 mb-8 leading-relaxed">
              Ready to transform your business with intelligent technology?
              Let's build the future together.
            </p>

            <div className="space-y-6 mb-8">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-blue-600 rounded-lg flex items-center justify-center flex-shrink-0">
                  <Mail className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h3 className="font-semibold text-slate-900 mb-1">Email</h3>
                  <a
                    href="mailto:contact@pseudoclan.com"
                    className="text-blue-600 hover:text-blue-700 transition-colors"
                  >
                    contact@pseudoclan.com
                  </a>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-blue-600 rounded-lg flex items-center justify-center flex-shrink-0">
                  <MapPin className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h3 className="font-semibold text-slate-900 mb-1">Address</h3>
                  <p className="text-slate-600">Gurugram, Haryana, India</p>
                </div>
              </div>
            </div>

            <div>
              <h3 className="font-semibold text-slate-900 mb-4">
                Connect With Us
              </h3>
              <div className="flex gap-4">
                <a
                  href="https://www.linkedin.com/in/vivek-k-3551b5360/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-12 h-12 bg-slate-100 hover:bg-blue-600 rounded-lg flex items-center justify-center transition-colors group"
                >
                  <Linkedin className="w-5 h-5 text-slate-700 group-hover:text-white transition-colors" />
                </a>
                <a
                  href="https://twitter.com/pseudo_clan"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-12 h-12 bg-slate-100 hover:bg-blue-600 rounded-lg flex items-center justify-center transition-colors group"
                >
                  <Twitter className="w-5 h-5 text-slate-700 group-hover:text-white transition-colors" />
                </a>
                <a
                  href="https://github.com/vivekpseudo"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-12 h-12 bg-slate-100 hover:bg-blue-600 rounded-lg flex items-center justify-center transition-colors group"
                >
                  <Github className="w-5 h-5 text-slate-700 group-hover:text-white transition-colors" />
                </a>
              </div>
            </div>
          </div>

          {/* Right Column - Contact Form */}
          <div className="bg-slate-50 rounded-2xl p-8 border border-slate-200">
            <h3 className="text-2xl font-bold text-slate-900 mb-6">
              Send Us a Message
            </h3>
            <form className="space-y-5 text-black">
              <div>
                <label
                  htmlFor="name"
                  className="block text-sm font-medium text-slate-700 mb-2"
                >
                  Your Name
                </label>
                <input
                  type="text"
                  id="name"
                  value={name}
                  onChange={(evt) => setName(evt.target.value)}
                  className="w-full px-4 py-3 bg-white border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="John Doe"
                />
              </div>

              <div>
                <label
                  htmlFor="email"
                  className="block text-sm font-medium text-slate-700 mb-2"
                >
                  Email Address
                </label>
                <input
                  type="email"
                  id="email"
                  value={email}
                  onChange={(evt) => setEmail(evt.target.value)}
                  className="w-full px-4 py-3 bg-white border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="john@example.com"
                />
              </div>

              <div>
                <label
                  htmlFor="company"
                  className="block text-sm font-medium text-slate-700 mb-2"
                >
                  Company Name
                </label>
                <input
                  value={companyName}
                  onChange={(evt) => setCompanyName(evt.target.value)}
                  type="text"
                  id="company"
                  className="w-full px-4 py-3 bg-white border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Your Company"
                />
              </div>

              <div>
                <label
                  htmlFor="message"
                  className="block text-sm font-medium text-slate-700 mb-2"
                >
                  Message
                </label>
                <textarea
                  value={message}
                  id="message"
                  rows={4}
                  onChange={(evt) => setMessage(evt.target.value)}
                  className="w-full px-4 py-3 bg-white border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                  placeholder="Tell us about your project..."
                />
              </div>

              <button
                onClick={() => {
                  if (!email || !name || !message) {
                    return;
                  }
                  try {
                    const res = emailjs.send(
                      "service_g8b88vp",
                      "template_03lw91v",
                      {
                        from_name: name,
                        to_name: "Pseudo",
                        message: `Company Name: ${companyName}. Message: ${message}`,
                        reply_to: email,
                      },
                      "LHtlBXUCQhcIDf6w2",
                    );
                    console.log("res", res);
                  } catch (e) {
                    console.log("error sening email", e);
                  }
                  alert(
                    "Thanks for contacting us, our dedicated team will response back within 24 hrs!",
                  );

                  setEmail("");
                  setMessage("");
                  setName("");
                  setCompanyName("");
                }}
                type="button"
                className="w-full px-6 py-4 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition-colors flex items-center justify-center gap-2 group"
              >
                Send Message
                <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </button>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}
// Footer
const Footer = () => {
  return (
    <footer
      id="contact"
      className="bg-black border-t border-gray-900 pt-16 pb-8"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col md:flex-row justify-between items-center mb-12">
          <div className="mb-8 md:mb-0 text-center md:text-left">
            <div className="flex items-center justify-center md:justify-start space-x-2 mb-4">
              <div className="w-8 h-8 bg-white-600 rounded-lg flex items-center justify-center p-[1px]">
                <img src="logoonly.png" className="App-logo" alt="logo" />
              </div>
              <span className="text-xl font-bold tracking-tight text-white">
                PSEUDOCLAN
              </span>
            </div>
            <p className="text-gray-500 max-w-xs">
              Innovating today for a smarter tomorrow.
            </p>
          </div>

          <div className="text-center md:text-right">
            <h4 className="text-white font-semibold mb-2">&nbsp;</h4>
           
            <p className="text-gray-500 text-sm">
            &copy; {new Date().getFullYear()} Pseudoclan Private Limited. All
            rights reserved.
          </p>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default function App() {
  // Initialize Firebase

  useEffect(() => {
    // Initialize n8n chat
    createChat({
      webhookUrl:
        "https://n8n.pseudoclan.com/webhook/4811c781-0c97-4c53-9fa0-35be0298d441/chat",
      chatInputKey: "chatInput",
      defaultLanguage: "en",
      target: "#n8n-chat",
      mode: "window",
      chatSessionKey: "sessionId",
      loadPreviousSession: true,
      metadata: {
        company: "PseudoClan Pvt Ltd",
        website: "https://pseudoclan.com",
        email: "vivek@pseudoclan.com",
      },
      showWelcomeScreen: false,
      initialMessages: [
        "Hi there! 👋",
        "My name is RepliQ. How can I assist you today?",
      ],
      i18n: {
        en: {
          title: "Welcome to PseudoClan!",
          subtitle: "Start a chat. We're here to help you 24/7.",
          footer: "",
          getStarted: "New Conversation",
          inputPlaceholder: "Type your question..",
          closeButtonTooltip: "close",
        },
        hi: {
          title: "नमस्ते! 👋",
          subtitle: "एक चैट शुरू करें। हम आपकी 24/7 मदद के लिए यहां हैं।",
          footer: "",
          getStarted: "नई बातचीत",
          inputPlaceholder: "अपना प्रश्न टाइप करें..",
          closeButtonTooltip: "बंद करें",
        },
      },
    });
  }, []);
  return (
    <div className="antialiased font-sans bg-gray-950 text-gray-100 selection:bg-blue-500 selection:text-white">
      <style>{`
                @import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;600;700&family=JetBrains+Mono:wght@400;700&display=swap');
                
                body {
                    font-family: 'Inter', sans-serif;
                }
                
                .font-mono {
                    font-family: 'JetBrains Mono', monospace;
                }
                
                .bg-grid {
                    background-size: 50px 50px;
                    background-image: linear-gradient(to right, rgba(255, 255, 255, 0.05) 1px, transparent 1px),
                                      linear-gradient(to bottom, rgba(255, 255, 255, 0.05) 1px, transparent 1px);
                }
                
                .glass-card {
                    background: rgba(17, 24, 39, 0.7);
                    backdrop-filter: blur(12px);
                    border: 1px solid rgba(255, 255, 255, 0.08);
                }
                
                .gradient-text {
                    background: linear-gradient(135deg, #60A5FA 0%, #A78BFA 100%);
                    -webkit-background-clip: text;
                    -webkit-text-fill-color: transparent;
                }
                
                .glow-hover:hover {
                    box-shadow: 0 0 20px rgba(96, 165, 250, 0.15);
                }

                html {
                    scroll-behavior: smooth;
                }
            `}</style>
      <Navbar />
      <Hero />
      <Products />
      <Innovation />
      <AboutCapabilities />
      <Capabilities />
      <Contact />
      <div id="n8n-chat"></div>
      <Footer />
    </div>
  );
}
